/**
 * Phase 2.5 — 가용 보고서 listing + 선택적 enqueue.
 *
 * - symbol 입력 (watchlist 에 있는 종목만 — paused 도 OK)
 * - pages 입력 (default 1 = 30개, max 20 = 600개)
 * - Fetch → status 별 보고서 표 (new / already_ingested / needs_broker_fetch)
 * - checkbox 로 선택 → "선택 N개 enqueue" → API call → toast
 *
 * - pdfUrl=null 인 row 는 disabled (미래 broker website fetch 대상)
 * - already_ingested 는 force=true 로 enqueue (재처리)
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type AvailableReport, type ReportsAvailable } from '../lib/api';
import { ErrorBox } from './Watchlist';

export default function AdminReportsPage() {
  const [search] = useSearchParams();
  const initialSymbol = search.get('symbol') ?? '';

  const [symbol, setSymbol] = useState(initialSymbol);
  const [pages, setPages] = useState(1);
  const [data, setData] = useState<ReportsAvailable | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [enqueueing, setEnqueueing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fetchListing = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(symbol)) {
      setError('symbol 은 6자리 숫자.');
      return;
    }
    setLoading(true);
    setError(null);
    setSelected(new Set());
    try {
      const d = await api.reportsAvailable(symbol, pages);
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (externalId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(externalId)) next.delete(externalId);
      else next.add(externalId);
      return next;
    });
  };

  const toggleAllNew = () => {
    if (!data) return;
    const newOnly = data.entries.filter((e) => e.status === 'new');
    const allSelected = newOnly.every((e) => selected.has(e.externalId));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        newOnly.forEach((e) => next.delete(e.externalId));
      } else {
        newOnly.forEach((e) => next.add(e.externalId));
      }
      return next;
    });
  };

  const enqueueSelected = async () => {
    if (!data || selected.size === 0) return;
    const entries = data.entries.filter((e) => selected.has(e.externalId));
    // new: /admin/enqueue (items 형식 — broker fetched PDF 포함).
    // already_ingested: /admin/regenerate-wiki (reportId 기반 진짜 reprocess path).
    const newOnes = entries.filter((e) => e.status === 'new' && e.pdfUrl);
    const reprocess = entries
      .filter((e) => e.status === 'already_ingested' && e.reportId)
      .map((e) => e.reportId as string);

    // 100-item batch (server-side schema max).
    const BATCH = 100;
    const chunks = <T,>(arr: T[]): T[][] => {
      const out: T[][] = [];
      for (let i = 0; i < arr.length; i += BATCH) out.push(arr.slice(i, i + BATCH));
      return out;
    };

    setEnqueueing(true);
    setToast(null);
    try {
      const results: string[] = [];
      if (newOnes.length > 0) {
        let okCount = 0;
        // entries 의 전체 metadata 보냄 — URL 매칭 skip.
        const items = newOnes.map((e) => ({
          sourceUrl: e.sourceUrl,
          pdfUrl: e.pdfUrl as string,
          externalId: e.externalId,
          source: e.source,
          symbol: e.symbol,
          title: e.title,
          broker: e.broker,
          publishedAt: e.publishedAt,
        }));
        for (const batch of chunks(items)) {
          // Phase A: 매뉴얼 enqueue 는 realtime only.
          const r = await api.enqueue(batch, false);
          okCount += r.results.filter((x) => x.status === 'enqueued').length;
        }
        results.push(`new: ${okCount}/${newOnes.length}`);
      }
      if (reprocess.length > 0) {
        let okCount = 0;
        for (const batch of chunks(reprocess)) {
          const r = await api.regenerateWiki(batch);
          const ritems = r.results ?? [];
          okCount += ritems.filter((x) => x.status === 'enqueued' || x.status === 'queued').length;
        }
        results.push(`재처리: ${okCount}/${reprocess.length}`);
      }
      setToast(`✅ enqueue 완료 — ${results.join(', ')}`);
      // refresh listing
      fetchListing();
    } catch (e) {
      setToast(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setEnqueueing(false);
    }
  };

  return (
    <section>
      <h1 className="text-2xl font-semibold tracking-tight">📥 가용 보고서</h1>
      <p className="mt-1 text-sm text-slate-500">
        네이버 리서치 listing 에서 선택적 다운로드. paused 종목도 가능.
      </p>

      <form onSubmit={fetchListing} className="mt-5 flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-xs text-slate-600">
          종목코드
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="005930"
            maxLength={6}
            className="mt-0.5 w-28 rounded border border-slate-300 px-2 py-1 font-mono text-sm focus:border-indigo-500 focus:outline-none"
          />
        </label>
        <label className="flex flex-col text-xs text-slate-600">
          pages (1=30개)
          <input
            type="number"
            value={pages}
            onChange={(e) => setPages(Math.min(20, Math.max(1, +e.target.value || 1)))}
            min={1}
            max={20}
            className="mt-0.5 w-20 rounded border border-slate-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:bg-slate-300"
        >
          {loading ? '조회 중…' : 'Fetch'}
        </button>
      </form>

      {error && <div className="mt-3"><ErrorBox msg={error} /></div>}

      {data && (
        <>
          <div className="mt-4 flex items-center justify-between text-sm">
            <p className="text-slate-600">
              <span className="font-semibold">{data.name}</span>{' '}
              <span className="font-mono text-xs text-slate-500">({data.symbol})</span> ·{' '}
              {data.total}건 · {data.pages} page
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleAllNew}
                className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50"
              >
                new 전체 선택/해제
              </button>
              <button
                onClick={enqueueSelected}
                disabled={selected.size === 0 || enqueueing}
                className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:bg-slate-300"
              >
                {enqueueing ? '처리 중…' : `선택 ${selected.size}개 enqueue`}
              </button>
            </div>
          </div>

          {toast && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-2 text-sm">
              {toast}
            </div>
          )}

          <table className="mt-3 w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-300 text-left text-xs uppercase text-slate-500">
                <th className="w-8 py-2"></th>
                <th className="py-2">제목</th>
                <th className="py-2">증권사</th>
                <th className="py-2 whitespace-nowrap">발행일</th>
                <th className="py-2">상태</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <Row
                  key={e.externalId}
                  entry={e}
                  selected={selected.has(e.externalId)}
                  onToggle={() => toggleSelect(e.externalId)}
                />
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="mt-10 rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500">
        📄 PDF 직접 업로드 (예정) — 네이버에 없는 해외 IB 보고서 (JP Morgan, Nomura 등) 업로드 path.
      </div>
    </section>
  );
}

function Row({
  entry,
  selected,
  onToggle,
}: {
  entry: AvailableReport;
  selected: boolean;
  onToggle: () => void;
}) {
  const canSelect = entry.status !== 'needs_broker_fetch';
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="py-2 align-top">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={!canSelect}
          className="h-4 w-4 disabled:opacity-30"
        />
      </td>
      <td className="py-2 align-top">
        <a
          href={entry.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-900 hover:text-indigo-600"
          title={entry.sourceUrl}
        >
          {entry.title}
        </a>
      </td>
      <td className="py-2 align-top text-slate-600">{entry.broker ?? '—'}</td>
      <td className="py-2 align-top whitespace-nowrap font-mono text-xs text-slate-500">
        {entry.publishedAt}
      </td>
      <td className="py-2 align-top">
        <StatusBadge entry={entry} />
      </td>
    </tr>
  );
}

function StatusBadge({ entry }: { entry: AvailableReport }) {
  if (entry.status === 'already_ingested') {
    return (
      <span className="pill bg-emerald-100 text-emerald-700" title="이미 수집된 보고서. 선택 시 재처리 (force)">
        ✓ 수집됨
      </span>
    );
  }
  if (entry.status === 'needs_broker_fetch') {
    return (
      <span className="pill bg-slate-100 text-slate-500" title="네이버에 PDF 첨부 없음. 미래 broker website 직접 fetch 예정">
        🚫 PDF 없음 (예정)
      </span>
    );
  }
  // status === 'new'
  return (
    <span className="inline-flex items-center gap-1">
      <span className="pill bg-indigo-100 text-indigo-700">신규</span>
      {entry.pdfUrlSource === 'shinhansec' && (
        <span
          className="pill bg-amber-100 text-amber-700 text-xs"
          title="네이버에 PDF 없어서 신한투자증권 mobile API 에서 PDF fetch"
        >
          📄 신한
        </span>
      )}
    </span>
  );
}
